import { TERMINAL_DATA } from '../constants/terminalConstants';
import { COLORI_MAPPINGS } from '../constants/colorConstants'; // Import COLORI_MAPPINGS for getColorsForLanguage

// Helper to get colors based on language, replicating original logic
const getColorsForLanguage = (lingua) => {
  return COLORI_MAPPINGS[lingua] || COLORI_MAPPINGS.it; // Default to Italian
};

export const validateTerminal = (terminalCode) => {
  if (!terminalCode || terminalCode.trim() === '') {
    return { isValid: false, error: 'Terminale vuoto' };
  }

  if (terminalCode.startsWith('SP') && terminalCode.length > 2) {
    // Nuovo formato SPx/y
    const match = terminalCode.match(/^SP(\d+)\/(\d+)$/);
    if (match) {
      const length = parseInt(match[1]);
      const peel = parseInt(match[2]);
      if (isNaN(length) || length < 0 || length > 100) {
        return { isValid: false, error: `Terminale SP con lunghezza non valida: ${match[1]}` };
      }
      if (isNaN(peel) || peel < 0 || peel > 50) {
        return { isValid: false, error: `Terminale SP con spelatura non valida: ${match[2]}` };
      }
      return { isValid: true, error: null };
    }

    // Fallback per formato vecchio SPx (compatibilità)
    const lengthPart = terminalCode.substring(2);
    const length = parseInt(lengthPart);
    if (isNaN(length) || length <= 0 || length > 100) {
      return { isValid: false, error: `Terminale SP con lunghezza non valida: ${lengthPart}` };
    }
    return { isValid: true, error: null };
  }

  if (!TERMINAL_DATA[terminalCode]) {
    return { isValid: false, error: `Terminale sconosciuto: ${terminalCode}` };
  }

  return { isValid: true, error: null };
};

export const validateColor = (color, lingua) => {
  if (!color || color.trim() === '') {
    return { isValid: false, error: 'Colore vuoto' };
  }

  const availableColors = getColorsForLanguage(lingua);
  if (!availableColors.includes(color)) {
    return { isValid: false, error: `Colore sconosciuto: ${color}` };
  }

  return { isValid: true, error: null };
};

export const checkIfCableIsDuplicate = (targetCable, cables) => {
  const duplicateIndexes = [];

  // Funzione helper per normalizzare i valori per il confronto (semplificata)
  const normalizeValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    return value;
  };


  cables.forEach((cable, index) => {
    // Controllo più preciso per evitare confronti con se stesso
    const isSameCable = (
      (cable === targetCable) || // Stesso riferimento oggetto (più affidabile)
      (cable.originalIndex !== undefined && targetCable.originalIndex !== undefined && cable.originalIndex === targetCable.originalIndex) // Stesso originalIndex solo se entrambi esistono
    );

    if (isSameCable) {
      duplicateIndexes.push(index); // Include se stesso
      return;
    }

    // Confronto dettagliato campo per campo
    const fieldComparisons = {
      siglatura: cable.siglatura === targetCable.siglatura,
      colore: cable.colore === targetCable.colore,
      sezione: cable.sezione === targetCable.sezione,
      lunghezza: cable.lunghezza === targetCable.lunghezza,
      terminaleA: cable.terminaleA === targetCable.terminaleA,
      terminaleB: cable.terminaleB === targetCable.terminaleB,
      collegamentoA: cable.collegamentoA === targetCable.collegamentoA,
      collegamentoB: cable.collegamentoB === targetCable.collegamentoB,
      gruppo: cable.gruppo === targetCable.gruppo,
      guaina: cable.guaina === targetCable.guaina
    };

    const areIdentical = Object.values(fieldComparisons).every(Boolean);

    if (areIdentical) {
      duplicateIndexes.push(index);
    }
  });

  const result = {
    isDuplicate: duplicateIndexes.length > 1,
    duplicateIndexes: duplicateIndexes
  };


  return result;
};


export const validateCable = (cable, cables, lingua) => {
  const issues = [];

  if (cable.poli && Array.isArray(cable.poli)) {
    if (!cable.nomeCavo || cable.nomeCavo.trim() === '') {
      issues.push('Nome cavo mancante');
    }

    cable.poli.forEach((polo, index) => {
      const termAValidation = validateTerminal(polo.terminaleA);
      if (!termAValidation.isValid) {
        issues.push(`Polo ${index + 1} - Terminale A: ${termAValidation.error}`);
      }

      const termBValidation = validateTerminal(polo.terminaleB);
      if (!termBValidation.isValid) {
        issues.push(`Polo ${index + 1} - Terminale B: ${termBValidation.error}`);
      }

      const colorValidation = validateColor(polo.colore, lingua);
      if (!colorValidation.isValid) {
        issues.push(`Polo ${index + 1} - Colore: ${colorValidation.error}`);
      }
    });
  } else { // Standard cable validation
    // Siglatura è obbligatoria per cavi standard se non siamo in modalità collegamento
    // Questo era il comportamento originale che causava "Problemi" se mancava la siglatura
    // Ma non era un "warning" bloccante
    // if (!cable.siglatura || cable.siglatura.trim() === '') {
    //   issues.push('Siglatura mancante');
    // }

    const termAValidation = validateTerminal(cable.terminaleA);
    if (!termAValidation.isValid) {
      issues.push(`Terminale A: ${termAValidation.error}`);
    }

    const termBValidation = validateTerminal(cable.terminaleB);
    if (!termBValidation.isValid) {
      issues.push(`Terminale B: ${termBValidation.error}`);
    }

    const colorValidation = validateColor(cable.colore, lingua);
    if (!colorValidation.isValid) {
      issues.push(`Colore: ${colorValidation.error}`);
    }

    if (!cable.sezione || cable.sezione <= 0) {
      issues.push('Sezione non valida');
    }

    // Controlla duplicati solo per cavi standard
    const duplicateCheck = checkIfCableIsDuplicate(cable, cables);
    if (duplicateCheck.isDuplicate) {
      const otherNumbers = duplicateCheck.duplicateIndexes
        .filter(idx => idx !== cable.originalIndex)
        .map(idx => `#${idx + 1}`)
        .join(', ');
      issues.push(`Cavo duplicato di: ${otherNumbers}`);
    }
  }

  return {
    isValid: issues.length === 0,
    issues: issues // Return issues to be displayed
  };
};

export const validateCableFields = (cable, cables, lingua) => {
  const missingFields = [];

  if (cable.poli && Array.isArray(cable.poli) && cable.poli.length > 0) {
    if (!cable.nomeCavo || cable.nomeCavo.trim() === '') {
      missingFields.push('nome cavo');
    }
    cable.poli.forEach((polo, index) => {
      if (!polo.collegamentoA || polo.collegamentoA.trim() === '') {
        missingFields.push(`collegamento A per polo ${index + 1}`);
      }
      if (!polo.collegamentoB || polo.collegamentoB.trim() === '') {
        missingFields.push(`collegamento B per polo ${index + 1}`);
      }
    });
  } else {
    if (!cable.siglatura || (typeof cable.siglatura === 'string' && cable.siglatura.trim() === '') || (typeof cable.siglatura !== 'string' && !cable.siglatura)) {
      missingFields.push('siglatura');
    }
    if (!cable.collegamentoA || cable.collegamentoA.trim() === '') {
      missingFields.push('collegamento A');
    }
    if (!cable.collegamentoB || cable.collegamentoB.trim() === '') {
      missingFields.push('collegamento B');
    }
  }
  return missingFields;
};